import type { SearchMarket } from '../api/searchTypes';

export type MarketSortKey =
    | 'default'
    | 'price'
    | 'volume'
    | 'question'
    | 'started'
    | 'closed'
    | 'resolved';

export const MARKET_SORT_KEYS: MarketSortKey[] = [
    'default',
    'price',
    'volume',
    'question',
    'started',
    'closed',
    'resolved',
];

export const MARKET_SORT_LABELS: Record<MarketSortKey, string> = {
    default: 'Best',
    price: 'Price',
    volume: 'Volume',
    question: 'Question',
    started: 'Started',
    closed: 'Closed',
    resolved: 'Resolved',
};

const DEPENDENT_MIN_MARKETS = 3;
const DEPENDENT_SUM_LOW = 0.9;
const DEPENDENT_SUM_HIGH = 1.1;

export function isDependent(markets: SearchMarket[]): boolean {
    if (markets.some(m => m.neg_risk)) return true;
    if (markets.length < DEPENDENT_MIN_MARKETS) return false;
    const total = markets.reduce((sum, m) => sum + (m.yes_prob ?? 0), 0);
    return total >= DEPENDENT_SUM_LOW && total <= DEPENDENT_SUM_HIGH;
}

export function pickPriceMarket(markets: SearchMarket[]): SearchMarket | null {
    const pool = markets.filter(m => m.is_open);
    const base = pool.length ? pool : markets;
    const priced = base.filter(m => m.yes_prob != null);
    if (!priced.length) return null;
    if (priced.length === 1) return priced[0];
    const dependent = isDependent(priced);
    return priced.reduce((best, m) => {
        const a = dependent ? [m.yes_prob ?? 0, m.volume] : [m.volume, m.yes_prob ?? 0];
        const b = dependent
            ? [best.yes_prob ?? 0, best.volume]
            : [best.volume, best.yes_prob ?? 0];
        if (a[0] !== b[0]) return a[0] > b[0] ? m : best;
        if (a[1] !== b[1]) return a[1] > b[1] ? m : best;
        return best;
    });
}

function time(value: string | null): number | null {
    if (!value) return null;
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? null : parsed;
}

function compareNullable(a: number | null, b: number | null, dir: 1 | -1): number {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return a === b ? 0 : (a > b ? -1 : 1) * dir;
}

function keyValue(market: SearchMarket, key: MarketSortKey): number | null {
    if (key === 'price') return market.yes_prob;
    if (key === 'volume') return market.volume;
    if (key === 'started') return time(market.effective_start_date);
    if (key === 'closed') return time(market.closed_date);
    if (key === 'resolved') return time(market.resolved_date);
    return null;
}

export function sortMarkets(
    markets: SearchMarket[],
    key: MarketSortKey,
    dir: 'asc' | 'desc',
): SearchMarket[] {
    const factor: 1 | -1 = dir === 'desc' ? 1 : -1;
    const out = [...markets];

    if (key === 'default') {
        const priceMarket = pickPriceMarket(markets);
        const dependent = isDependent(markets.filter(m => m.yes_prob != null));
        out.sort((a, b) => {
            if (a === priceMarket) return -1;
            if (b === priceMarket) return 1;
            if (a.matched !== b.matched) return a.matched ? -1 : 1;
            if (a.is_open !== b.is_open) return a.is_open ? -1 : 1;
            const primary = dependent
                ? compareNullable(a.yes_prob, b.yes_prob, 1)
                : compareNullable(a.volume, b.volume, 1);
            if (primary !== 0) return primary;
            return a.market_id.localeCompare(b.market_id);
        });
        return out;
    }

    if (key === 'question') {
        out.sort((a, b) => {
            const text = (a.question ?? '').localeCompare(b.question ?? '');
            if (text !== 0) return text * -factor;
            return a.market_id.localeCompare(b.market_id);
        });
        return out;
    }

    out.sort((a, b) => {
        const value = compareNullable(keyValue(a, key), keyValue(b, key), factor);
        if (value !== 0) return value;
        return a.market_id.localeCompare(b.market_id);
    });
    return out;
}